import { GOOGLE_FONT_CATALOG, type GoogleFontEntry } from './google-fonts.catalog';

/**
 * Loads a Google font the moment the page uses it.
 *
 * The Font dropdowns offer the whole catalogue (~1,950 families, as Figma
 * does). Loading them all up front would be megabytes of CSS, so instead the
 * page is watched: any font-family in an inline style or a CSS variable on an
 * element (`style="font-family: Geist"`, `--nt-font: 'Geist', sans-serif`) gets
 * its stylesheet added once. Families already linked in index.html are skipped.
 *
 * Call once at startup; later calls are no-ops.
 */
export function startGoogleFontAutoload(doc: Document = document): void {
  if (started || typeof MutationObserver === 'undefined') return;
  started = true;
  document_ = doc;
  catalog = new Map(GOOGLE_FONT_CATALOG.map((entry) => [entry[0].toLowerCase(), entry]));

  // Families index.html already loads.
  doc.querySelectorAll<HTMLLinkElement>('link[href*="fonts.googleapis.com/css"]').forEach((link) => {
    new URL(link.href).searchParams.getAll('family').forEach((spec) => loaded.add(spec.split(':')[0].toLowerCase()));
  });

  scanTree(doc.documentElement);
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes') pending.add(mutation.target as Element);
      else mutation.addedNodes.forEach((node) => node.nodeType === 1 && pending.add(node as Element));
    });
    schedule();
  }).observe(doc.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['style'] });
}

/** Load the Google families named in a font stack now, e.g. for a canvas chart. */
export function ensureGoogleFonts(stack: string | null | undefined): void {
  if (!started || !stack) return;
  familiesOf(stack).forEach(load);
}

let started = false;
let document_: Document;
let catalog = new Map<string, GoogleFontEntry>();
const loaded = new Set<string>();
const pending = new Set<Element>();
const seenStyles = new Set<string>();
let frame = 0;

function schedule(): void {
  if (frame) return;
  frame = requestAnimationFrame(() => {
    frame = 0;
    const elements = [...pending];
    pending.clear();
    elements.forEach(scanTree);
  });
}

function scanTree(root: Element): void {
  scanStyle(root.getAttribute('style'));
  root.querySelectorAll('[style]').forEach((element) => scanStyle(element.getAttribute('style')));
}

function scanStyle(style: string | null): void {
  if (!style || seenStyles.has(style)) return;
  // Styles repeat a lot (drag / resize rewrite them); cache, but stay bounded.
  if (seenStyles.size > 5000) seenStyles.clear();
  seenStyles.add(style);
  style.split(';').forEach((declaration) => {
    const colon = declaration.indexOf(':');
    if (colon > 0) familiesOf(declaration.slice(colon + 1)).forEach(load);
  });
}

function familiesOf(value: string): string[] {
  return value
    .replace(/!important/i, '')
    .split(',')
    .map((part) => part.trim().replace(/^["']|["']$/g, '').toLowerCase())
    .filter((name) => catalog.has(name) && !loaded.has(name));
}

function load(name: string): void {
  if (loaded.has(name)) return;
  loaded.add(name);
  const [family, , weights] = catalog.get(name)!;
  const link = document_.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, '+')}${weights ? `:wght@${weights}` : ''}&display=swap`;
  document_.head.appendChild(link);
}
